import React, { useEffect, useState } from 'react';
import { IAnalyticsFilters, IAnalyticsProductsResponse } from '@/interfaces/Dashboard/dashboard.interface';
import { AnalyticsExportButton } from './AnalyticsExportButton';
import { CommonCard } from '@/components/common/CommonCard';
import { SectionCard } from '@/components/common/SectionCard';
import { Package, DollarSign, Layers, Tag, TrendingUp, TrendingDown, Clock, Zap } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { CommonCardSkeleton, DonutChartSkeleton } from '@/components/ui/Loaders/DashboardSkeletons';
import { EmptyChart } from '@/components/common/EmptyChart';
import { NoDataFound } from '@/components/common/NoDataFound';
import CommonTable from '@/components/common/CommonTable';
import { formatRand } from '@/utility/helper';

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];

interface ProductSalesSectionProps {
    filters: IAnalyticsFilters;
    data: IAnalyticsProductsResponse['data'] | null;
    isLoading: boolean;
}


export const ProductSalesSection: React.FC<ProductSalesSectionProps> = ({ filters, data, isLoading }) => {
    
    if (isLoading) {
        return (
            <div className="space-y-6 animate-pulse">
                 <div className="flex justify-between items-center mb-4">
                    <div className="h-6 w-32 bg-gray-200 rounded"></div>
                    <div className="h-8 w-24 bg-gray-200 rounded"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {Array.from({ length: 4 }).map((_, i) => <CommonCardSkeleton key={i} />)}
                </div>
            </div>
        );
    }

    const kpis = data?.kpis || {} as any;
    const charts = data?.charts || {} as any;
    const tables = data?.tables || {} as any;

    const kpiCards = [
        { label: 'Total Products Sold', value: kpis.totalProductsSold ?? 0, icon: <Package />, color: '#3B82F6' },
        { label: 'Product Revenue', value: formatRand(kpis.revenueFromProducts), icon: <DollarSign />, color: '#10B981' },
        { label: 'Avg Product Price', value: formatRand(kpis.avgProductPrice), icon: <Tag />, color: '#F59E0B' },
        { label: 'Total Categories', value: kpis.totalCategories ?? 0, icon: <Layers />, color: '#8B5CF6' },
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-foreground">Product Sales</h2>
                <AnalyticsExportButton
                    exportUrl="/reports/analytics/export?type=products"
                    fileName="product_sales.xlsx"
                    filters={filters}
                />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {kpiCards.map((card, i) => (
                    <CommonCard key={i} label={card.label} value={card.value} icon={card.icon} iconColor={card.color} />
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <SectionCard title="Top Selling Products" icon={<TrendingUp />}>
                    <div className="h-[300px] w-full">
                        {!charts.topSellingProducts || charts.topSellingProducts.length === 0 ? (
                            <EmptyChart height="h-[300px]" />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={charts.topSellingProducts} layout="vertical" margin={{ left: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E5E7EB" />
                                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
                                <YAxis type="category" dataKey="productName" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#374151', fontWeight: 500 }} width={120} />
                                <Tooltip
                                    cursor={{ fill: '#F3F4F6' }}
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                />
                                <Bar dataKey="unitsSold" name="Units Sold" fill="#10B981" radius={[0, 4, 4, 0]} barSize={24} />
                            </BarChart>
                        </ResponsiveContainer>
                        )}
                    </div>
                </SectionCard>

                <SectionCard title="Sales by Category" icon={<Layers />}>
                    <div className="h-[300px] w-full flex items-center justify-center">
                        {!charts.salesByCategory || charts.salesByCategory.length === 0 ? (
                            <EmptyChart height="h-[300px]" />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={charts.salesByCategory}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={70}
                                        outerRadius={100}
                                        paddingAngle={5}
                                        dataKey="revenue"
                                        nameKey="category"
                                    >
                                        {charts.salesByCategory.map((_, index) => (
                                            <Cell key={`cell-\${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                        formatter={(value: number) => [formatRand(value), 'Revenue']}
                                    />
                                    <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }}/>
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </SectionCard>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <SectionCard title="Top 10 Products" className="overflow-x-auto">
                    <CommonTable 
                        columns={[
                            { key: 'productName', header: 'Product', className: 'font-medium text-foreground' },
                            { key: 'category', header: 'Category', className: 'text-muted-foreground' },
                            { key: 'unitsSold', header: 'Units', className: 'text-right', headerClassName: 'text-right' },
                            { key: 'revenue', header: 'Revenue', render: (row: any) => formatRand(row.revenue), className: 'text-right font-bold text-success', headerClassName: 'text-right' }
                        ]}
                        data={tables.top10Products || []}
                    />
                </SectionCard>
                
                <SectionCard title="Lowest Selling Products" className="overflow-x-auto">
                    <CommonTable 
                        columns={[
                            { key: 'productName', header: 'Product', className: 'font-medium text-foreground' },
                            { key: 'category', header: 'Category', className: 'text-muted-foreground' },
                            { key: 'unitsSold', header: 'Units', className: 'text-right', headerClassName: 'text-right' },
                            { key: 'revenue', header: 'Revenue', render: (row: any) => formatRand(row.revenue), className: 'text-right font-medium text-muted-foreground', headerClassName: 'text-right' }
                        ]}
                        data={tables.lowestProducts || []}
                    />
                </SectionCard>
            </div>
        </div>
    );
};
